import * as React from "react";
import { useMemo } from "react";
import {
  Hammer,
  MessageCircle,
  Settings,
  MessageSquareCode,
  BookOpen,
  FlaskConical,
  Workflow,
  ListOrdered,
  ListTodo,
  Loader2,
  Cable,
} from "lucide-react";
import { useFeatureFlagEnabled } from "@/lib/feature-flags";
import { track } from "@/lib/analytics";

import { NavMain } from "@/components/sidebar/nav-main";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useAuthState } from "@/data/user";
import { useUpdateNotification } from "@/hooks/useUpdateNotification";
import { Button } from "@sphinx/design-system/button";
import { Skeleton } from "@sphinx/design-system/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@sphinx/design-system/tooltip";
import { HOSTED_MODE } from "@/lib/config";
import { SphinxSessionBadge } from "@/components/sphinx-sign-in";
import {
  isHostedTabBlocked,
  normalizeHostedHashTab,
} from "@/lib/hosted-tab-policy";

interface NavItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
  disabledTooltip?: string;
  /** Optional pill shown next to the label, e.g. "New" */
  badge?: string;
  /** Only show this item when the named feature flag is enabled */
  featureFlag?: string;
  /** Hide this item when the named feature flag is enabled */
  hiddenByFlag?: string;
  /** Extra tab ids that should also highlight this item as active */
  matchTabs?: string[];
}

interface NavSection {
  id: string;
  items: NavItem[];
}

/**
 * Every flag key the sidebar actually RESOLVES into the `featureFlags` map
 * inside `MCPSidebar`. A nav item's `featureFlag` / `hiddenByFlag` must appear
 * here or the item is invisible forever: `filterByFeatureFlags` reads
 * `flags[key]` as `undefined`, treats it as off, and nothing ever calls the
 * flag — so PostHog shows it as never evaluated and the cause looks like a
 * rollout problem rather than a missing map entry. The Sessions item shipped
 * exactly that way. `mcp-sidebar-feature-flags.test.ts` fails if the two lists
 * drift again.
 */
export const SIDEBAR_RESOLVED_FLAG_KEYS = [
  "sandboxes-enabled",
  "hosts-enabled",
] as const;

/**
 * Filter navigation items based on active feature flags.
 * Items with `featureFlag` are shown only when that flag is enabled.
 * Items with `hiddenByFlag` are hidden when that flag is enabled.
 *
 * A key missing from `flags` counts as OFF — see
 * {@link SIDEBAR_RESOLVED_FLAG_KEYS}.
 */
export function filterByFeatureFlags(
  sections: NavSection[],
  flags: Record<string, boolean>
): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (item.featureFlag && !flags[item.featureFlag]) return false;
        if (item.hiddenByFlag && flags[item.hiddenByFlag]) return false;
        return true;
      }),
    }))
    .filter((section) => section.items.length > 0);
}

// Define sections with their respective items.
// Exported so tests can assert against the real nav data (e.g. that Skills is
// not a sidebar item — it lives in the Connect tab switcher).
export const navigationSections: NavSection[] = [
  {
    id: "connection",
    items: [
      {
        // sphinx: the Playground is the home page (`/` redirects here).
        title: "Playground",
        url: "/playground",
        icon: MessageCircle,
      },
      {
        // sphinx: ONE page. The fixed 10-target execution matrix (was mcpjam
        // "Connect" / Hosts, then a separate Clients page) and the MCP
        // connectors are the same surface: the header picks the client, the
        // cards below are that client's connectors. Route `/connectors`;
        // `/clients`, `/hosts` and `/servers` redirect here. The data name
        // stays targets and the component names stay Host*.
        title: "Client Connectors",
        url: "/connectors",
        icon: Cable,
        matchTabs: ["clients", "host-compare", "skills"],
      },
    ],
  },
  {
    id: "mcp-apps",
    items: [
      {
        title: "Evaluate",
        url: "/evals",
        icon: FlaskConical,
      },
      {
        // sphinx: runner fleet + job queue behind the runs.
        title: "Queue",
        url: "/queue",
        icon: ListOrdered,
      },
    ],
  },
  {
    id: "others",
    items: [
      // Skills is not a sidebar item: it's execution-context config, so it
      // lives as a Connect tab (Servers | Client | Computer | Skills) and is
      // reached through that switcher.
      // {
      //   title: "Tracing",
      //   url: "/tracing",
      //   icon: Activity,
      // },
    ],
  },
  {
    id: "debuggers",
    items: [
      {
        title: "OAuth Debugger",
        url: "/oauth-flow",
        icon: Workflow,
      },
    ],
  },
  {
    id: "primitives",
    items: [
      {
        title: "Tools",
        url: "/tools",
        icon: Hammer,
      },
      {
        title: "Resources",
        url: "/resources",
        icon: BookOpen,
      },
      {
        title: "Prompts",
        url: "/prompts",
        icon: MessageSquareCode,
      },
      {
        title: "Tasks",
        url: "/tasks",
        icon: ListTodo,
      },
    ],
  },
];

// Footer utility icons for users without an account menu (signed-out): the
// account dropdown normally hosts Settings/Support, so signed-in users only
// see the notification bell here. Same list for local and hosted guests.
const signedOutUtilityItems: NavItem[] = [
  {
    title: "Settings",
    url: "/settings",
    icon: Settings,
  },
];

// Neutral placeholder shown while auth is resolving, so signed-in users don't
// see the signed-out nav list flash before their authed items appear.
function SidebarNavSkeleton() {
  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {Array.from({ length: 8 }).map((_, i) => (
            <SidebarMenuItem key={i}>
              <SidebarMenuButton disabled>
                <Skeleton className="size-4 rounded" />
                <Skeleton className="h-3.5 w-24 group-data-[collapsible=icon]:hidden" />
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

/**
 * Drop the nav items a hosted deployment cannot serve. Only `hostedBlocked`
 * surfaces are dropped: this filter runs BEFORE `filterByFeatureFlags`, so
 * anything it removes is gone with no flag able to bring it back — which is
 * how the Sessions item stayed invisible on app.mcpjam.com (#4210) while it
 * was an allow-list.
 */
export function getHostedNavigationSections(
  sections: NavSection[]
): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.flatMap((item) => {
        const normalizedTab = normalizeHostedHashTab(
          item.url.replace(/^[#/]+/, "")
        );

        if (isHostedTabBlocked(normalizedTab)) {
          return [];
        }

        return [item];
      }),
    }))
    .filter((section) => section.items.length > 0);
}

const hostedNavigationSections =
  getHostedNavigationSections(navigationSections);

interface MCPSidebarProps extends React.ComponentProps<typeof Sidebar> {
  onNavigate?: (section: string) => void;
  activeTab?: string;
}

export function MCPSidebar({ onNavigate, activeTab, ...props }: MCPSidebarProps) {
  const sandboxesEnabled = useFeatureFlagEnabled("sandboxes-enabled");
  const { isAuthenticated, isLoading: authResolving } = useAuthState();
  const { status: updateStatus, restartAndInstall } = useUpdateNotification();
  const showUpdateButton =
    updateStatus.kind === "pending" || updateStatus.kind === "downloaded";
  const updateInstalling =
    updateStatus.kind === "pending" && updateStatus.installRequested;
  const handleUpdateClick = () => {
    if (!updateInstalling) {
      restartAndInstall();
    }
  };
  const { state, isMobile } = useSidebar();

  const handleNavClick = (url: string) => {
    if (onNavigate && /^[#/]/.test(url)) {
      const section = url.replace(/^[#/]+/, "");
      track("sidebar_nav_clicked", {
        location: "mcp_sidebar",
        section,
      });
      onNavigate(section);
    } else {
      window.open(url, "_blank");
    }
  };
  const featureFlags = useMemo(
    () => ({
      "sandboxes-enabled": !!sandboxesEnabled && isAuthenticated,
      // Hosts/Connect is fully rolled out; its nav visibility is purely
      // auth-driven (signed-out users keep the legacy Servers item).
      "hosts-enabled": isAuthenticated,
    }),
    [
      sandboxesEnabled,
      isAuthenticated,
    ]
  );
  // The logo goes home — Playground is the home page.
  const hubNavHash = "#playground";
  const visibleNavigationSections = filterByFeatureFlags(
    HOSTED_MODE ? hostedNavigationSections : navigationSections,
    featureFlags
  );

  // No account menu in sphinx: Settings/Support live as footer utility icons.
  const utilityItems = signedOutUtilityItems;

  const isNavItemActive = (item: NavItem) =>
    normalizeHostedHashTab(
      item.url.replace(/^[#/]+/, "").split("/")[0] || "connectors"
    ) === activeTab ||
    (activeTab !== undefined && (item.matchTabs?.includes(activeTab) ?? false));

  return (
    <>
      <Sidebar collapsible="icon" {...props}>
        <SidebarHeader className="gap-1 px-2 pt-1.5 pb-2">
          <div
            className={cn(
              "no-drag",
              state === "collapsed" && !isMobile && "flex justify-center px-0"
            )}
          >
            {isMobile ? (
              <button
                type="button"
                onClick={() => handleNavClick(hubNavHash)}
                className="flex w-full cursor-pointer items-center justify-center px-4 py-3 transition-opacity hover:opacity-80"
              >
                <img
                  src="/sphinx.svg"
                  alt="Sphinx"
                  className="h-4 w-auto"
                />
              </button>
            ) : state === "expanded" ? (
              <div className="relative isolate w-full">
                <button
                  type="button"
                  onClick={() => handleNavClick(hubNavHash)}
                  className={cn(
                    "relative z-0 flex w-full cursor-pointer items-center justify-center py-2 transition-opacity duration-200",
                    /* Reserve space for the collapse control so the logo stays visually centered and
                       clicks on the logo never compete with the invisible hit target. */
                    "px-2 pr-10 hover:opacity-80"
                  )}
                >
                  <img
                    src="/sphinx.svg"
                    alt="Sphinx"
                    className="h-4 w-auto"
                  />
                </button>
                <SidebarTrigger
                  className={cn(
                    "absolute top-1/2 right-0 z-20 size-7 -translate-y-1/2 shrink-0",
                    /* pointer-events must stay enabled: if we use pointer-events-none until hover,
                       a click can lose :hover before mouseup/click (Electron / fast moves) and the
                       event never reaches this button. Touch has no hover — use coarse-pointer rule. */
                    "pointer-events-auto opacity-0 transition-opacity duration-200",
                    /* Named group avoids ambiguous group-hover when SidebarProvider also uses group/sidebar-wrapper */
                    "group-hover/sidebar-rail:opacity-100 focus-visible:opacity-100",
                    "[@media(hover:none)]:opacity-100"
                  )}
                  aria-label="Collapse sidebar"
                />
              </div>
            ) : (
              <SidebarTrigger
                className="size-7 shrink-0"
                aria-label="Expand sidebar"
              />
            )}
          </div>
          {showUpdateButton && (
            <div className="px-3 pt-2">
              <Button
                size="sm"
                onClick={handleUpdateClick}
                aria-disabled={updateInstalling}
                className={cn(
                  "h-5 w-full gap-1 rounded-full bg-primary px-2 text-[11px] font-medium text-primary-foreground hover:bg-primary/90",
                  updateInstalling && "pointer-events-none hover:bg-primary"
                )}
              >
                {updateInstalling && (
                  <Loader2 className="size-2.5 animate-spin" aria-hidden />
                )}
                {updateInstalling ? "Updating…" : "Update"}
              </Button>
            </div>
          )}
        </SidebarHeader>
        <SidebarContent className="gap-0">
          {authResolving ? (
            <SidebarNavSkeleton />
          ) : (
            visibleNavigationSections.map((section, sectionIndex) => {
              return (
                <React.Fragment key={section.id}>
                  <NavMain
                    items={section.items.map((item) => ({
                      ...item,
                      isActive: isNavItemActive(item),
                    }))}
                    onItemClick={handleNavClick}
                  />
                  {/* Add subtle divider between sections (except after the last section) */}
                  {sectionIndex < visibleNavigationSections.length - 1 && (
                    <div className="mx-4 my-1 border-t border-border/50" />
                  )}
                </React.Fragment>
              );
            })
          )}
        </SidebarContent>
        <SidebarFooter>
          {utilityItems.length > 0 ? (
            <div className="flex items-center gap-1 px-1 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:px-0">
              {utilityItems.map((item) => (
                <Tooltip key={item.title}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={item.title}
                      onClick={() => handleNavClick(item.url)}
                      className={cn(
                        "flex size-7 items-center justify-center rounded-md text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                        isNavItemActive(item) &&
                          "bg-sidebar-accent text-sidebar-accent-foreground"
                      )}
                    >
                      {item.icon ? <item.icon className="size-4" /> : null}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top">{item.title}</TooltipContent>
                </Tooltip>
              ))}
            </div>
          ) : null}
          <SphinxSessionBadge />
        </SidebarFooter>
      </Sidebar>
    </>
  );
}
